import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import type { SetupRole } from './capabilitySetup';
import { ActionButton, BODY, Chip, ChipLine, FONT, LINE, RowGroupLabel, TEXT, TEXT_3 } from './designSystem';

export interface AppliesValue {
  roleIds: string[];
  allRoles: boolean;
}

const toggled = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

/**
 * Who one of the org admin's shared capabilities applies to: every role (those
 * added later too), or the roles chosen, team by team. A role whose team has a
 * capability of its own by the same name can't be chosen: one name means one
 * capability in a team.
 */
const AppliesToDialog: React.FC<{
  open: boolean;
  capabilityName: string;
  roles: SetupRole[];
  teams: Array<{ id: string; name: string }>;
  value: AppliesValue;
  /** Roles it can't apply to (their team has its own capability by this name). */
  clashing: Set<string>;
  onClose: () => void;
  onSave: (value: AppliesValue) => void;
}> = ({ open, capabilityName, roles, teams, value, clashing, onClose, onSave }) => {
  const [choice, setChoice] = useState<AppliesValue>(value);
  useEffect(() => {
    if (open) setChoice(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const teamIds = teams.filter((t) => roles.some((r) => r.teamId === t.id));
  const clash = roles.some((r) => clashing.has(r.id));
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 640,
          maxWidth: '92vw',
          maxHeight: '86vh',
          bgcolor: '#1C1C1C',
          border: `1px solid ${LINE}`,
          borderRadius: '8px',
          backgroundImage: 'none',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box sx={{ px: '20px', pt: '18px', pb: '12px', borderBottom: `1px solid ${LINE}` }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: TEXT }}>
          Who is {capabilityName || 'this capability'} for?
        </Typography>
        <Typography sx={{ ...BODY, fontSize: 12.5, mt: '4px' }}>
          It becomes a required capability of every role you choose, set by you: their teams see it and its levels, and
          can’t change them.
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: '20px', py: '14px' }}>
        <Box sx={{ pb: '12px', borderBottom: `1px solid ${LINE}` }}>
          <Chip
            multi
            label="All roles, including future ones"
            selected={choice.allRoles}
            disabled={clash}
            reason="A team has a capability of its own by this name, so it can't apply to every role."
            onClick={() => setChoice((c) => ({ ...c, allRoles: !c.allRoles }))}
          />
        </Box>
        <Box sx={choice.allRoles ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
          {teamIds.length === 0 && (
            <Typography sx={{ ...BODY, fontSize: 12.5, color: TEXT_3, pt: '12px' }}>
              No roles yet. Teams add their roles in Capabilities.
            </Typography>
          )}
          {teamIds.map((team) => {
            const teamRoles = roles.filter((r) => r.teamId === team.id);
            return (
              <Box
                key={team.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  py: '12px',
                  borderBottom: `1px solid ${LINE}`
                }}
              >
                <RowGroupLabel>{team.name}</RowGroupLabel>
                <ChipLine>
                  {teamRoles.map((r) => (
                    <Chip
                      key={r.id}
                      multi
                      label={r.name}
                      selected={choice.roleIds.includes(r.id)}
                      disabled={clashing.has(r.id)}
                      reason={`${team.name} has a capability of its own called ${capabilityName}.`}
                      onClick={() => setChoice((c) => ({ ...c, roleIds: toggled(c.roleIds, r.id) }))}
                    />
                  ))}
                </ChipLine>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', p: '12px 20px', borderTop: `1px solid ${LINE}` }}
      >
        <ActionButton onClick={onClose}>Cancel</ActionButton>
        <ActionButton
          primary
          onClick={() =>
            onSave({ allRoles: choice.allRoles, roleIds: choice.roleIds.filter((id) => !clashing.has(id)) })
          }
        >
          Done
        </ActionButton>
      </Box>
    </Dialog>
  );
};

export default AppliesToDialog;
